<template>
  <div class="shadow-otp-panel" data-testid="shadow-otp-panel">
    <div class="shadow-header">
      <span class="shadow-header-label">Shadow OTP</span>
      <span
        class="info-icon"
        data-testid="info-icon-section"
        @click.stop="toggleInfo('section')">i</span>
    </div>
    <div
      v-if="activeInfoKey === 'section'"
      class="info-panel"
      data-testid="info-panel-section">
      <p>{{ infoText.section }}</p>
    </div>

    <p class="shadow-copy">
      Shadow strategies run in parallel to Live OTP but deal nothing. Toggling
      one off suspends its tick; toggling on re-arms.
    </p>

    <!-- Allocator status card -->
    <div v-if="status?.allocator" class="allocator-card" data-testid="allocator-card">
      <div class="allocator-head">
        <div class="allocator-title">
          Shared Pool Allocator
          <span
            class="info-icon"
            data-testid="info-icon-allocator"
            @click.stop="toggleInfo('allocator')">i</span>
        </div>
        <div class="allocator-sub">
          K = {{ status.allocator.totalSlots }} ·
          pool £{{ formatGbp(status.allocator.poolNotionalGbp) }} ·
          slot £{{ formatGbp(status.allocator.slotNotionalGbp) }}
        </div>
      </div>
      <div
        v-if="activeInfoKey === 'allocator'"
        class="info-panel info-panel--allocator"
        data-testid="info-panel-allocator">
        <p>{{ infoText.allocator }}</p>
      </div>
      <div class="allocator-slots">
        <div
          v-for="slot in allocatorSlots"
          :key="slot.slotIndex"
          :class="['allocator-slot', slot.holder ? 'allocator-slot--busy' : 'allocator-slot--free']"
          data-testid="allocator-slot">
          <span class="slot-index">Slot {{ slot.slotIndex }}</span>
          <span class="slot-holder">{{ slot.holder ?? 'free' }}</span>
        </div>
      </div>
    </div>

    <!-- Per-book table -->
    <div class="books-wrap">
      <div class="books-head">
        <span class="col col-name">
          Book
          <span
            class="info-icon"
            data-testid="info-icon-book"
            @click.stop="toggleInfo('book')">i</span>
        </span>
        <span class="col col-inst">
          Instrument
          <span
            class="info-icon"
            data-testid="info-icon-instrument"
            @click.stop="toggleInfo('instrument')">i</span>
        </span>
        <span class="col col-dir">
          Dir.
          <span
            class="info-icon"
            data-testid="info-icon-direction"
            @click.stop="toggleInfo('direction')">i</span>
        </span>
        <span class="col col-status">
          Status
          <span
            class="info-icon"
            data-testid="info-icon-status"
            @click.stop="toggleInfo('status')">i</span>
        </span>
        <span class="col col-toggle">
          Kill switch
          <span
            class="info-icon"
            data-testid="info-icon-killswitch"
            @click.stop="toggleInfo('killswitch')">i</span>
        </span>
        <span class="col col-decision">
          Last decision
          <span
            class="info-icon"
            data-testid="info-icon-decision"
            @click.stop="toggleInfo('decision')">i</span>
        </span>
        <span class="col col-pos">
          Open position
          <span
            class="info-icon"
            data-testid="info-icon-position"
            @click.stop="toggleInfo('position')">i</span>
        </span>
        <span class="col col-sharpe">
          Study Sharpe
          <span
            class="info-icon"
            data-testid="info-icon-sharpe"
            @click.stop="toggleInfo('sharpe')">i</span>
        </span>
      </div>
      <div
        v-if="activeColumnInfoKey"
        class="info-panel info-panel--column"
        data-testid="info-panel-column">
        <p><strong>{{ activeColumnLabel }}:</strong> {{ infoText[activeColumnInfoKey] }}</p>
      </div>
      <div
        v-for="book in status?.books ?? []"
        :key="book.key"
        class="book-row"
        :data-testid="`shadow-book-row-${book.key}`">
        <div class="col col-name">
          {{ book.name }}
        </div>
        <div class="col col-inst">
          <span class="chip chip-instrument">{{ book.instrument }}</span>
        </div>
        <div class="col col-dir">
          <span class="chip chip-direction">{{ book.direction.toUpperCase() }}</span>
        </div>
        <div class="col col-status">
          <span
            :class="['pill', book.effectiveEnabled ? 'pill-armed' : 'pill-killed']"
            :data-testid="`shadow-status-pill-${book.key}`">
            {{ book.effectiveEnabled ? 'ARMED' : 'KILL-SWITCHED' }}
          </span>
        </div>
        <div class="col col-toggle">
          <template v-if="isSuperUser">
            <select
              class="kill-select"
              :data-testid="`kill-switch-select-${book.key}`"
              :value="overrideValueFor(book)"
              @change="onOverrideChange(book.key, ($event.target as HTMLSelectElement).value)">
              <option value="file">File default ({{ book.enabledFileConfig ? 'on' : 'off' }})</option>
              <option value="on">Force ON</option>
              <option value="off">Force OFF</option>
            </select>
          </template>
          <template v-else>
            <span class="chip chip-readonly" :data-testid="`kill-switch-readonly-${book.key}`">
              {{ overrideLabelFor(book) }}
            </span>
          </template>
        </div>
        <div class="col col-decision">
          <template v-if="book.lastDecisionRow">
            <span class="date">{{ book.lastDecisionRow.date }}</span>
            <span v-if="book.lastDecisionRow.fired" class="fired">FIRED</span>
            <span v-else class="skip">{{ book.lastDecisionRow.skipReason ?? 'no fire' }}</span>
          </template>
          <template v-else>
            <span class="muted">—</span>
          </template>
          <div
            v-if="book.crosscheck"
            :class="['xcheck', crosscheckClass(book.crosscheck)]"
            :title="book.crosscheck.error ?? book.crosscheck.latestDivergence ?? book.crosscheck.summary ?? ''"
            :data-testid="`shadow-xcheck-${book.key}`">
            {{ crosscheckLabel(book.crosscheck) }}
          </div>
        </div>
        <div class="col col-pos">
          <template v-if="book.currentOpenPosition">
            <div class="pos-line">
              entry {{ book.currentOpenPosition.entryDate ?? '—' }}
            </div>
            <div class="pos-line muted-sub">
              synth {{ book.currentOpenPosition.synthPrice?.toFixed(2) ?? '—' }} ·
              slot
              <template v-if="book.currentOpenPosition.allocatorSlotIndex !== null">
                {{ book.currentOpenPosition.allocatorSlotIndex }}
              </template>
              <template v-else>fallback</template>
            </div>
          </template>
          <template v-else>
            <span class="muted">—</span>
          </template>
        </div>
        <div class="col col-sharpe">
          {{ book.studyExpectedSharpe.toFixed(2) }}
        </div>
      </div>
      <div v-if="!status" class="loading" data-testid="shadow-loading">Loading shadow status…</div>
    </div>

    <div v-if="lastError" class="error-banner" data-testid="shadow-error">
      {{ lastError }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api } from '../api/client'
import { useAuth } from '../composables/useAuth'

type LastDecisionRow = {
  date: string
  fired: boolean
  skipReason: string | null
}

type OpenPosition = {
  entryDate: string | null
  synthPrice: number | null
  allocatorSlotIndex: number | null
}

type AllocatorSlot = {
  slotIndex: number
  holder: string | null
  expectedExitDate: string | null
  notionalGbp: number
}

type ShadowBook = {
  key: string
  name: string
  instrument: string
  direction: string
  enabledFileConfig: boolean
  killSwitchOverride: string | null
  effectiveEnabled: boolean
  lastDecisionRow: LastDecisionRow | null
  currentOpenPosition: OpenPosition | null
  lastFireAtUtc: string | null
  studyExpectedSharpe: number
  allocatorSlot: AllocatorSlot | null
  crosscheck?: Crosscheck | null
}

// Nightly 12:15 UK scripts/*_shadow_crosscheck.py run (ShadowCrosscheckService).
type Crosscheck = {
  runDate: string
  runAtUtc: string
  divergences: number
  summary: string | null
  latestDivergence: string | null
  error: string | null
}

function crosscheckLabel(x: Crosscheck): string {
  const day = x.runDate.slice(5)
  if (x.error) return `x-check error · ${day}`
  if (x.divergences > 0) return `x-check ${x.divergences} diverge · ${day}`
  return `x-check OK · ${day}`
}

function crosscheckClass(x: Crosscheck): string {
  if (x.error) return 'xcheck--error'
  return x.divergences > 0 ? 'xcheck--diverge' : 'xcheck--ok'
}

type ShadowStatus = {
  books: ShadowBook[]
  allocator: {
    freeSlots: number
    busySlots: number
    totalSlots: number
    poolNotionalGbp: number
    slotNotionalGbp: number
    holders: AllocatorSlot[]
  }
}

const { isSuperUser } = useAuth()
const status = ref<ShadowStatus | null>(null)
const lastError = ref<string | null>(null)

// ─── info tooltips ────────────────────────────────────────────────
// Click-to-toggle popovers, matching the pattern in ExecPanel.vue
// (small (i) glyph, info-panel div revealed below). Only one is open
// at a time; clicking outside the panel or pressing Esc closes it.

type InfoKey =
  | 'section'
  | 'allocator'
  | 'book'
  | 'instrument'
  | 'direction'
  | 'status'
  | 'killswitch'
  | 'decision'
  | 'position'
  | 'sharpe'

const columnInfoKeys: readonly InfoKey[] = [
  'book', 'instrument', 'direction', 'status',
  'killswitch', 'decision', 'position', 'sharpe',
] as const

const columnLabels: Record<InfoKey, string> = {
  section: 'Shadow OTP',
  allocator: 'Shared Pool Allocator',
  book: 'Book',
  instrument: 'Instrument',
  direction: 'Dir.',
  status: 'Status',
  killswitch: 'Kill switch',
  decision: 'Last decision',
  position: 'Open position',
  sharpe: 'Study Sharpe',
}

const infoText: Record<InfoKey, string> = {
  section:
    'Seven shadow strategies run every tick in parallel with the Live OTP book — each one evaluates its trigger, logs a decision row, and books a paper position when it fires. They deal nothing (no HL order, no dealing fees). Their purpose is to accumulate a live track record for graduation to Live OTP. Contrarian Fade — NQ v2 is the 2026-09-06 follow-up to CF-NQ (winner of the PR #413 pre-registered promotion study, sized at £25k per PR #414 Paired Y study) — see docs/analysis/2026-09-06-cf-nq-v2-verification.md and docs/analysis/2026-09-06-cf-nq-paired-portfolio-study.md.',
  allocator:
    'The Shared Pool Allocator mediates a £76,000 capital pool across four slots of £19,000 each. When a shadow strategy fires, it claims one free slot. If all four are busy, its trigger is denied and the decision row is written with skip_reason=capital_slot_busy. Rolling back the allocator falls back to £50k per-book independent capital (spec §15 step 2a).',
  book:
    'The shadow strategy — an evaluator that reads live market data every tick and books paper trades on its own trigger. Each book has a frozen parameter set (from its pre-registered study) and cannot be re-tuned without a new pre-registration pass.',
  instrument:
    'The leveraged ETP the book trades. LQQ3 = 3x NASDAQ-100 long (WisdomTree BOOST). 3LGO = 3x physical gold long (WisdomTree Gold 3x Daily Leveraged, GBP line). 3LOI = 3x WTI crude oil long (WisdomTree WTI Crude Oil 3x Daily Leveraged, GBP line).',
  direction:
    'Trade direction. All seven shadow books are long-only. Short-side candidates were tombstoned at Part-0 for gold, oil, and natgas because the underlying assets carry structural upward drift that punishes systematic shorts.',
  status:
    'Effective armed state. ARMED (green) means the tick loop will evaluate the trigger every ~5 minutes on LSE trading days. KILL-SWITCHED (grey) means the evaluator is currently suspended. The status combines the file-config default with the Redis override (Redis wins).',
  killswitch:
    'Override the file-config default without a redeploy. File default (on) honours the appsettings.json value (all seven switches default true — CF-NQ and CF-NQ v2 have independent kill switches). Force ON writes config:override:{book}_shadow_enabled=true to Redis. Force OFF writes false — the evaluator skips its tick immediately. Delete the override (choose File default) to restore file-truth. Super-user only.',
  decision:
    'The most recent row from data/{book}_shadow/decisions.csv. Every LSE trading day writes exactly one row per book, whether it fires or skips. signal_not_met / hold_open / capital_slot_busy are healthy skip reasons; a missing row for today means the evaluator isn’t ticking. The x-check line under it is the nightly 12:15 UK cross-check (scripts/*_shadow_crosscheck.py) of the book’s rows against independently recomputed inputs — graduation needs zero unexplained divergences; hover for the latest one.',
  position:
    'The book’s currently-held paper position, if any. Empty means the book is flat. Shows entry date, entry synth price, and the allocator slot index holding the £19k notional.',
  sharpe:
    'The book’s per-trade Sharpe from its original study (pre-frozen-parameter selection). CF-NQ 0.58, CF-NQ v2 0.94 (OOS Sharpe, PR #413 pre-registered promotion study), VT-Long m5 0.43, VT-Long m10 0.48, Gold Vol Spike m3 0.39, Oil Contrarian Fade 0.56, Oil VIX Tail Long 0.39. This is a floor expectation — actual shadow performance may differ.',
}

const activeInfoKey = ref<InfoKey | null>(null)
const activeColumnInfoKey = computed<InfoKey | null>(() => {
  const k = activeInfoKey.value
  return k && (columnInfoKeys as readonly InfoKey[]).includes(k) ? k : null
})
const activeColumnLabel = computed<string>(() =>
  activeColumnInfoKey.value ? columnLabels[activeColumnInfoKey.value] : '',
)

function toggleInfo(key: InfoKey) {
  activeInfoKey.value = activeInfoKey.value === key ? null : key
}

function closeInfo() {
  activeInfoKey.value = null
}

function onDocumentClick(ev: MouseEvent) {
  if (!activeInfoKey.value) return
  const target = ev.target as HTMLElement | null
  if (!target) return
  // Keep open when clicking the trigger icon or inside a rendered info-panel.
  if (target.closest('.info-icon') || target.closest('.info-panel')) return
  closeInfo()
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') closeInfo()
}

const allocatorSlots = computed<AllocatorSlot[]>(() => {
  const s = status.value?.allocator
  if (!s) return []
  const byIndex = new Map<number, AllocatorSlot>()
  for (const h of s.holders) byIndex.set(h.slotIndex, h)
  const out: AllocatorSlot[] = []
  for (let i = 0; i < s.totalSlots; i++) {
    out.push(byIndex.get(i) ?? { slotIndex: i, holder: null, expectedExitDate: null, notionalGbp: s.slotNotionalGbp })
  }
  return out
})

function formatGbp(v: number): string {
  if (!Number.isFinite(v)) return '—'
  return v >= 1000 ? Math.round(v).toLocaleString('en-GB') : v.toFixed(2)
}

function overrideValueFor(book: ShadowBook): string {
  if (book.killSwitchOverride === 'true') return 'on'
  if (book.killSwitchOverride === 'false') return 'off'
  return 'file'
}

function overrideLabelFor(book: ShadowBook): string {
  if (book.killSwitchOverride === 'true') return 'FORCED ON'
  if (book.killSwitchOverride === 'false') return 'FORCED OFF'
  return `default: ${book.enabledFileConfig ? 'on' : 'off'}`
}

async function loadStatus() {
  try {
    status.value = await api.otpShadowStatus()
    lastError.value = null
  } catch (e) {
    lastError.value = `Failed to load shadow status: ${(e as Error).message ?? e}`
  }
}

async function onOverrideChange(bookKey: string, next: string) {
  let setTo: boolean | null
  if (next === 'on') setTo = true
  else if (next === 'off') setTo = false
  else setTo = null
  try {
    await api.otpShadowKillSwitch({ book: bookKey, setTo })
    await loadStatus()
  } catch (e) {
    lastError.value = `Kill-switch flip failed: ${(e as Error).message ?? e}`
    await loadStatus() // re-sync from server truth
  }
}

// Poll every 30s while the tab is active + visible. Stops when the tab
// component is unmounted (parent switches to Live) or when the page tab
// is hidden (visibilitychange), so an OTP screen left open in a background
// tab doesn't burn requests.
let pollInterval: ReturnType<typeof setInterval> | null = null
function startPolling() {
  stopPolling()
  pollInterval = setInterval(loadStatus, 30_000)
}
function stopPolling() {
  if (pollInterval) { clearInterval(pollInterval); pollInterval = null }
}
function onVisibilityChange() {
  if (typeof document === 'undefined') return
  if (document.hidden) stopPolling()
  else { loadStatus(); startPolling() }
}

onMounted(() => {
  loadStatus()
  startPolling()
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange)
    document.addEventListener('click', onDocumentClick)
    document.addEventListener('keydown', onKeydown)
  }
})

onUnmounted(() => {
  stopPolling()
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    document.removeEventListener('click', onDocumentClick)
    document.removeEventListener('keydown', onKeydown)
  }
})
</script>

<style scoped>
.shadow-otp-panel { padding: 0.5rem 0; }

.shadow-header {
  display: flex; align-items: center; gap: 0.35rem;
  margin: 0 0 0.35rem 0;
}
.shadow-header-label {
  font-weight: 600; color: #e0e0e0; font-size: 0.95rem;
  letter-spacing: 0.02em;
}

.shadow-copy {
  color: #aaa; font-size: 0.85rem; margin: 0 0 0.75rem 0;
}

.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.6rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
  flex: 0 0 auto;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }

.info-panel {
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 6px;
  padding: 0.6rem 0.85rem;
  margin: 0 0 0.75rem 0;
  font-size: 0.82rem;
  color: #8b949e;
  line-height: 1.55;
}
.info-panel p { margin: 0; }
.info-panel strong { color: #c9d1d9; }
.info-panel--allocator { margin: 0.5rem 0 0.5rem 0; }
.info-panel--column { margin: 0.35rem 0 0.35rem 0; }

.allocator-card {
  background: #1a1a1a; border: 1px solid #333; border-radius: 6px;
  padding: 0.75rem; margin-bottom: 1rem;
}
.allocator-head { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; margin-bottom: 0.5rem; }
.allocator-title { font-weight: 600; color: #e0e0e0; font-size: 0.95rem; }
.allocator-sub { color: #888; font-size: 0.78rem; }
.allocator-slots { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.allocator-slot {
  padding: 0.35rem 0.7rem; border-radius: 4px; font-size: 0.8rem;
  display: inline-flex; gap: 0.5rem; align-items: baseline;
  border: 1px solid #333;
}
.allocator-slot--free { background: #14251a; color: #74c48b; border-color: #2a4a30; }
.allocator-slot--busy { background: #14202a; color: #78a8e0; border-color: #2a3a4a; }
.slot-index { font-family: 'JetBrains Mono', monospace; font-weight: 600; }
.slot-holder { font-family: 'JetBrains Mono', monospace; opacity: 0.9; }

.books-wrap { background: #1a1a1a; border: 1px solid #333; border-radius: 6px; padding: 0.5rem; }

.books-head, .book-row {
  display: grid;
  grid-template-columns: 1.8fr 0.7fr 0.5fr 1fr 1.3fr 1.2fr 1.3fr 0.7fr;
  gap: 0.5rem; align-items: center;
  padding: 0.45rem 0.4rem;
}
.books-head { color: #888; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 1px solid #333; }
.book-row { border-bottom: 1px solid #232323; font-size: 0.82rem; color: #e0e0e0; }
.book-row:last-child { border-bottom: none; }

.chip {
  display: inline-block; padding: 0.15rem 0.5rem; border-radius: 3px;
  font-size: 0.72rem; letter-spacing: 0.04em;
}
.chip-instrument { background: #22283a; color: #b8c4e0; }
.chip-direction { background: #14251a; color: #74c48b; font-weight: 600; }
.chip-readonly { background: #232323; color: #aaa; }

.pill {
  display: inline-block; padding: 0.2rem 0.55rem; border-radius: 4px;
  font-weight: 700; font-size: 0.72rem; letter-spacing: 0.06em;
}
.pill-armed { background: #14351d; color: #74c48b; border: 1px solid #2a5b34; }
.pill-killed { background: #2a2a2a; color: #b8b8b8; border: 1px solid #d1a842; }

.kill-select {
  background: #0f0f0f; color: #e0e0e0; border: 1px solid #333;
  border-radius: 3px; padding: 0.25rem 0.4rem; font-size: 0.78rem;
  font-family: inherit;
}
.kill-select:hover { border-color: #555; }

.date { font-family: 'JetBrains Mono', monospace; color: #aaa; margin-right: 0.35rem; }
.fired { color: #4caf50; font-weight: 600; letter-spacing: 0.03em; }
.skip { color: #888; font-style: italic; }
.xcheck { font-size: 0.72rem; margin-top: 2px; }
.xcheck--ok { color: #6a8f6c; }
.xcheck--diverge { color: #e0a030; font-weight: 600; }
.xcheck--error { color: #e0a030; font-style: italic; }
.muted { color: #666; }
.muted-sub { color: #888; font-size: 0.75rem; }
.pos-line { line-height: 1.2; }

.loading { padding: 1rem; color: #888; text-align: center; }
.error-banner {
  margin-top: 0.75rem;
  background: #2a1414; border: 1px solid #5a2020; color: #ffb0b0;
  padding: 0.6rem 0.9rem; border-radius: 4px; font-size: 0.85rem;
}

@media (max-width: 900px) {
  .books-head { display: none; }
  .book-row {
    grid-template-columns: 1fr 1fr;
    gap: 0.4rem 0.9rem;
    padding: 0.6rem 0.4rem;
  }
  .col::before {
    content: attr(data-label);
    color: #666; font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.05em;
    margin-right: 0.5rem;
  }
}
</style>
